export type ProductCategory =
  | 'Complete Systems'
  | 'Bundles'
  | 'Modules'
  | 'Accessories'
export type Season =
  | 'Lawn Care'
  | 'Snow Removal'
  | 'Leaf Cleanup'
  | 'All-Season'
export type StockStatus = 'in-stock' | 'pre-order' | 'notify'

export interface Product {
  id: string
  slug: string
  name: string
  category: ProductCategory
  season: Season[]
  price: number
  status: StockStatus
  shortDescription: string
  features: string[]
  includes: string[]
  image: string
  requiresCore?: boolean
  isPopular?: boolean
}

export const products: Product[] = [
  // === COMPLETE SYSTEMS ===
  {
    id: 'yarbo-lawn-mower',
    slug: 'yarbo-lawn-mower',
    name: 'Yarbo Lawn Mower',
    category: 'Complete Systems',
    season: ['Lawn Care'],
    price: 4199,
    status: 'in-stock',
    shortDescription:
      'Wire-free robotic lawn mower with multi-zone management for properties up to 6 acres.',
    features: [
      'Smart object detection',
      'Wire-free setup with app-defined virtual boundaries',
      'Multi-zone management',
      'Mows up to 6 acres twice per week',
      'Handles slopes up to 35 degrees (70%)',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Module',
      'Docking Station',
      'RTK Antenna',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Lawn_Mower.png?v=1777093280&width=1445',
    isPopular: true,
  },
  {
    id: 'yarbo-lawn-mower-pro',
    slug: 'yarbo-lawn-mower-pro',
    name: 'Yarbo Lawn Mower Pro',
    category: 'Complete Systems',
    season: ['Lawn Care'],
    price: 5599,
    status: 'in-stock',
    shortDescription:
      'High-performance mowing with dual 300W motors built for demanding lawns and steep slopes.',
    features: [
      'Dual 300W motors',
      '2500W peak cutting power',
      'Optional straight blades available',
      'Mows up to 6 acres',
      'Handles 70% / 35-degree slopes',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Pro Module',
      'Docking Station',
      'RTK Antenna',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Lawn_Mower_Pro.png?v=1777093280&width=1445',
    isPopular: true,
  },
  {
    id: 'yarbo-snow-blower',
    slug: 'yarbo-snow-blower',
    name: 'Yarbo Snow Blower',
    category: 'Complete Systems',
    season: ['Snow Removal'],
    price: 4999,
    status: 'in-stock',
    shortDescription:
      'Autonomous 2-stage snow clearing system built for serious winter conditions.',
    features: [
      'Autonomous snow clearing',
      '2-stage snow blower design',
      'Clears up to 12 inches of snow',
      '24-inch clearing width',
      'Auto recharge and resume functionality',
    ],
    includes: [
      'Yarbo Core',
      'Snow Blower Module',
      'Docking Station',
      'RTK Antenna',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Snow_Blower.png?v=1777093280&width=1445',
    isPopular: true,
  },
  {
    id: 'yarbo-blower',
    slug: 'yarbo-blower',
    name: 'Yarbo Blower',
    category: 'Complete Systems',
    season: ['Leaf Cleanup'],
    price: 4799,
    status: 'in-stock',
    shortDescription:
      'Robotic leaf and debris clearing with 21 N blowing force for wooded properties.',
    features: [
      'Robotic leaf and debris clearing',
      '21 N blowing force',
      'Handles slopes up to 35 degrees (70%)',
      '70-minute continuous runtime per charge',
      'App-controlled clearing zones',
    ],
    includes: ['Yarbo Core', 'Blower Module', 'Docking Station', 'RTK Antenna'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Blower.png?v=1777093280&width=1445',
    isPopular: true,
  },

  // === BUNDLES ===
  {
    id: 'yarbo-all-season-pro-bundle',
    slug: 'yarbo-all-season-pro-bundle',
    name: 'Yarbo Lawn Mower Pro + Snow Blower + Blower',
    category: 'Bundles',
    season: ['All-Season'],
    price: 7499,
    status: 'in-stock',
    shortDescription:
      'The ultimate all-season system. One Yarbo Core with Pro mowing, snow clearing, and leaf blower modules.',
    features: [
      'Complete year-round yard maintenance',
      'Pro-level mowing power (2500W peak)',
      '2-stage snow clearing',
      'High-force leaf blowing',
      'Seamless module swapping',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Pro Module',
      'Snow Blower Module',
      'Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_4-in-1_Yard_Robot_with_Lawn_Mower_Pro_Module.png?v=1777093281&width=1445',
    isPopular: true,
  },
  {
    id: 'yarbo-all-season-bundle',
    slug: 'yarbo-all-season-bundle',
    name: 'Yarbo Lawn Mower + Snow Blower + Blower',
    category: 'Bundles',
    season: ['All-Season'],
    price: 5999,
    status: 'in-stock',
    shortDescription:
      'All-season yard automation with mowing, snow clearing, and leaf blower modules.',
    features: [
      'Year-round yard automation',
      'Standard mowing module',
      '2-stage snow clearing',
      'High-force leaf blowing',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Module',
      'Snow Blower Module',
      'Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_4-in-1_Yard_Robot.png?v=1777093281&width=1445',
    isPopular: true,
  },
  {
    id: 'yarbo-lawn-mower-pro-snow-blower',
    slug: 'yarbo-lawn-mower-pro-snow-blower',
    name: 'Yarbo Lawn Mower Pro + Snow Blower',
    category: 'Bundles',
    season: ['Lawn Care', 'Snow Removal'],
    price: 6699,
    status: 'in-stock',
    shortDescription:
      'Pro-level mowing and serious snow clearing in one modular system.',
    features: [
      'Dual 300W mowing motors',
      '2-stage snow clearing',
      'Handles steep slopes',
      'Auto recharge and resume',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Pro Module',
      'Snow Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Lawn_Mower_Pro_Snow_Blower.png?v=1777093281&width=1445',
  },
  {
    id: 'yarbo-snow-blower-blower',
    slug: 'yarbo-snow-blower-blower',
    name: 'Yarbo Snow Blower + Blower',
    category: 'Bundles',
    season: ['Snow Removal', 'Leaf Cleanup'],
    price: 5999,
    status: 'in-stock',
    shortDescription:
      'Winter and fall bundle combining snow clearing and leaf management.',
    features: [
      '2-stage snow clearing',
      '21 N blowing force',
      'Long-driveway ready',
    ],
    includes: [
      'Yarbo Core',
      'Snow Blower Module',
      'Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Snow_Blower_Blower.png?v=1777093296&width=1445',
  },
  {
    id: 'yarbo-lawn-mower-snow-blower',
    slug: 'yarbo-lawn-mower-snow-blower',
    name: 'Yarbo Lawn Mower + Snow Blower',
    category: 'Bundles',
    season: ['Lawn Care', 'Snow Removal'],
    price: 5199,
    status: 'in-stock',
    shortDescription:
      'Standard dual-season bundle for mowing and snow clearing.',
    features: [
      'Wire-free mowing up to 6 acres',
      '2-stage snow clearing',
      'App-controlled scheduling',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Module',
      'Snow Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Snow_Blower_Lawn_Mower.png?v=1777093281&width=1445',
  },
  {
    id: 'yarbo-lawn-mower-blower',
    slug: 'yarbo-lawn-mower-blower',
    name: 'Yarbo Lawn Mower + Blower',
    category: 'Bundles',
    season: ['Lawn Care', 'Leaf Cleanup'],
    price: 4999,
    status: 'in-stock',
    shortDescription:
      'Spring through fall bundle for mowing and leaf clearing.',
    features: [
      'Wire-free mowing',
      '21 N blowing force',
      'Multi-zone management',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Module',
      'Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Lawn_Mower_Blower.png?v=1777093281&width=1445',
  },
  {
    id: 'yarbo-lawn-mower-pro-blower',
    slug: 'yarbo-lawn-mower-pro-blower',
    name: 'Yarbo Lawn Mower Pro + Blower',
    category: 'Bundles',
    season: ['Lawn Care', 'Leaf Cleanup'],
    price: 6499,
    status: 'in-stock',
    shortDescription:
      'Pro-level mowing and powerful leaf clearing for demanding properties.',
    features: [
      'Dual 300W mowing motors',
      '21 N blowing force',
      'Handles slopes up to 70%',
    ],
    includes: [
      'Yarbo Core',
      'Lawn Mower Pro Module',
      'Blower Module',
      'Docking Station',
    ],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Lawn_Mower_Pro_Blower.png?v=1777093280&width=1445',
  },

  // === MODULES (require Yarbo Core) ===
  {
    id: 'lawn-mower-module',
    slug: 'lawn-mower-module',
    name: 'Lawn Mower Module (Y Series)',
    category: 'Modules',
    season: ['Lawn Care'],
    price: 999,
    status: 'in-stock',
    requiresCore: true,
    shortDescription:
      'Module-only: add standard mowing to an existing Yarbo Core.',
    features: [
      'Requires Yarbo Core',
      'Multi-zone management',
      'Wire-free operation',
    ],
    includes: ['Lawn Mower Module only'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Lawn_Mower_Module.png?v=1777093279&width=1445',
  },
  {
    id: 'lawn-mower-pro-module',
    slug: 'lawn-mower-pro-module',
    name: 'Lawn Mower Pro Module (Y Series)',
    category: 'Modules',
    season: ['Lawn Care'],
    price: 2099,
    status: 'in-stock',
    requiresCore: true,
    shortDescription:
      'Module-only: upgrade an existing Yarbo Core with high-performance dual-motor mowing.',
    features: [
      'Requires Yarbo Core',
      'Dual 300W motors',
      '2500W peak cutting power',
    ],
    includes: ['Lawn Mower Pro Module only'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Lawn_Mower_Pro_Module.png?v=1777093279&width=1445',
  },
  {
    id: 'snow-blower-module',
    slug: 'snow-blower-module',
    name: 'Snow Blower Module (Y Series)',
    category: 'Modules',
    season: ['Snow Removal'],
    price: 1299,
    status: 'in-stock',
    requiresCore: true,
    shortDescription:
      'Module-only: add 2-stage snow clearing to an existing Yarbo Core.',
    features: [
      'Requires Yarbo Core',
      '2-stage design',
      '24-inch clearing width',
    ],
    includes: ['Snow Blower Module only'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Snow_Blower_Module.png?v=1777093280&width=1445',
  },
  {
    id: 'blower-module',
    slug: 'blower-module',
    name: 'Blower Module (Y Series)',
    category: 'Modules',
    season: ['Leaf Cleanup'],
    price: 1099,
    status: 'in-stock',
    requiresCore: true,
    shortDescription:
      'Module-only: add high-force leaf and debris clearing to an existing Yarbo Core.',
    features: [
      'Requires Yarbo Core',
      '21 N blowing force',
      'App-controlled zones',
    ],
    includes: ['Blower Module only'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Blower_Module.png?v=1777093279&width=1445',
  },
  {
    id: 'yarbo-trimmer-module',
    slug: 'yarbo-trimmer-module',
    name: 'Trimmer Module (Y Series)',
    category: 'Modules',
    season: ['Lawn Care'],
    price: 799,
    status: 'in-stock',
    requiresCore: true,
    shortDescription:
      'Module-only: hands-free edge trimming with Yarbo Trimmer and Back Brace Mount.',
    features: [
      'Requires Yarbo Core',
      'Connects through Back Brace Mount',
      'Automatic line feed',
      'Adjustable trimming height (2–4 inches)',
    ],
    includes: ['Yarbo Trimmer', 'Back Brace Mount'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Trimmer_Package.png?v=1777093279&width=1445',
  },
  {
    id: 'smart-assist-module',
    slug: 'smart-assist-module',
    name: 'Smart Assist Module (Y Series)',
    category: 'Modules',
    season: ['All-Season'],
    price: 299,
    status: 'in-stock',
    requiresCore: true,
    shortDescription:
      'Module-only: enhance your Yarbo Core with smart assist capabilities.',
    features: ['Requires Yarbo Core', 'Y Series compatible'],
    includes: ['Smart Assist Module only'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },

  // === ACCESSORIES ===
  {
    id: 'snow-plow-blade',
    slug: 'snow-plow-blade',
    name: 'Snow Plow Blade (Y Series)',
    category: 'Accessories',
    season: ['Snow Removal'],
    price: 159,
    status: 'in-stock',
    shortDescription: 'Snow plow blade accessory for Yarbo Y Series systems.',
    features: ['Y Series compatible'],
    includes: ['Snow Plow Blade'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Snow_Blower_Module.png?v=1777093280&width=1445',
  },
  {
    id: 'straight-blades-bolts',
    slug: 'straight-blades-bolts',
    name: 'Straight Blades & Bolts (Y Series)',
    category: 'Accessories',
    season: ['Lawn Care'],
    price: 90,
    status: 'in-stock',
    shortDescription:
      'Replacement straight blades and bolts for Yarbo Y Series mowing modules.',
    features: ['Y Series compatible'],
    includes: ['Straight blades and bolts'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Lawn_Mower_Module.png?v=1777093279&width=1445',
  },
  {
    id: 'cutting-blades-40',
    slug: 'cutting-blades-40',
    name: 'Cutting Blades & Bolts - 40 Pcs (Y Series)',
    category: 'Accessories',
    season: ['Lawn Care'],
    price: 39,
    status: 'in-stock',
    shortDescription:
      '40-piece set of cutting blades and bolts for Yarbo mowers.',
    features: ['Y Series compatible', '40-piece set'],
    includes: ['40 cutting blades and bolts'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Lawn_Mower_Module.png?v=1777093279&width=1445',
  },
  {
    id: 'cutting-disc-bolts',
    slug: 'cutting-disc-bolts',
    name: 'Cutting Disc & Bolts (Y Series)',
    category: 'Accessories',
    season: ['Lawn Care'],
    price: 29,
    status: 'in-stock',
    shortDescription:
      'Cutting disc and bolts for Yarbo Y Series mowing modules.',
    features: ['Y Series compatible'],
    includes: ['Cutting disc and bolts'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Lawn_Mower_Module.png?v=1777093279&width=1445',
  },
  {
    id: 'rtk-antenna',
    slug: 'rtk-antenna',
    name: 'RTK Antenna for Data Center & Rover (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 30,
    status: 'in-stock',
    shortDescription: 'RTK antenna for the Yarbo Data Center and Rover.',
    features: ['Y Series compatible'],
    includes: ['RTK Antenna'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
  {
    id: 'high-rtk-antenna-mount',
    slug: 'high-rtk-antenna-mount',
    name: 'High RTK Antenna Mount (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 26,
    status: 'in-stock',
    shortDescription: 'Elevated mount for the Yarbo RTK antenna.',
    features: ['Y Series compatible'],
    includes: ['High RTK Antenna Mount'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
  {
    id: 'back-brace-mount',
    slug: 'back-brace-mount',
    name: 'Back Brace Mount (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 149,
    status: 'in-stock',
    shortDescription:
      'Back Brace Mount accessory for connecting Yarbo modules.',
    features: ['Y Series compatible'],
    includes: ['Back Brace Mount'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Yarbo_Trimmer_Package.png?v=1777093279&width=1445',
  },
  {
    id: 'shear-pins',
    slug: 'shear-pins',
    name: 'Shear Pins & Cotter Pins Set (Set of 8) (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 29,
    status: 'in-stock',
    shortDescription: 'Set of 8 shear pins and cotter pins for Yarbo Y Series.',
    features: ['Y Series compatible', 'Set of 8'],
    includes: ['Shear pins and cotter pins (set of 8)'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
  {
    id: 'track-grease',
    slug: 'track-grease',
    name: 'Yarbo Track Grease (200 ml)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 49,
    status: 'in-stock',
    shortDescription: 'Track grease for Yarbo Y Series systems.',
    features: ['Y Series compatible', '200 ml'],
    includes: ['Track grease (200 ml)'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
  {
    id: 'tow-hitch',
    slug: 'tow-hitch',
    name: 'Tow Hitch (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 69,
    status: 'in-stock',
    shortDescription: 'Tow hitch accessory for Yarbo Y Series.',
    features: ['Y Series compatible'],
    includes: ['Tow Hitch'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
  {
    id: 'halow-antenna',
    slug: 'halow-antenna',
    name: 'HaLow Antenna for Rover (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 22,
    status: 'in-stock',
    shortDescription: 'HaLow antenna for the Yarbo Rover.',
    features: ['Y Series compatible'],
    includes: ['HaLow Antenna'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
  {
    id: 'core-cover',
    slug: 'core-cover',
    name: 'Core Cover (Y Series)',
    category: 'Accessories',
    season: ['All-Season'],
    price: 39,
    status: 'in-stock',
    shortDescription: 'Protective cover for the Yarbo Core.',
    features: ['Y Series compatible'],
    includes: ['Core Cover'],
    image:
      'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
  },
]

export const getProductBySlug = (slug: string) =>
  products.find((p) => p.slug === slug)
export const getProductsByCategory = (category: ProductCategory) =>
  products.filter((p) => p.category === category)
export const getProductsBySeason = (season: Season) =>
  products.filter(
    (p) => p.season.includes(season) || p.season.includes('All-Season'),
  )
export const getPopularProducts = () => products.filter((p) => p.isPopular)

// Stock status helpers
export const stockLabel = (status: StockStatus) => {
  switch (status) {
    case 'in-stock':
      return 'In Stock'
    case 'pre-order':
      return 'Pre-Order'
    case 'notify':
      return 'Notify Me'
  }
}

export const ctaLabel = (status: StockStatus) => {
  switch (status) {
    case 'in-stock':
      return 'Add to Cart'
    case 'pre-order':
      return 'Pre-Order'
    case 'notify':
      return 'Notify Me'
  }
}
